import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { useUpdateComponentOwnData } from '../../hooks/api/components';
import { InplaceEditContext } from './ComponentDetailsPageInplaceEditContext';

type ComponentDetailsPageInplaceEditProviderProps = {
  children: React.ReactNode;
};

export const ComponentDetailsPageInplaceEditProvider = ({
  children,
}: ComponentDetailsPageInplaceEditProviderProps) => {
  const [componentId, setComponentId] = useState<string | null>(null);

  const [activeFieldId, setActiveFieldId] = useState<string | null>(null);

  const queryClient = useQueryClient();

  const activate = (fieldId: string) => {
    // Prevent activation of the same field
    if (activeFieldId === fieldId) {
      return;
    }

    // Prevent activation when saving another field
    if (isSaving) {
      return;
    }

    setActiveFieldId(fieldId);
  };

  const deactivate = () => {
    setActiveFieldId(null);
  };

  const handleSave = async <T,>(componentId: string, fieldId: string, newValue: T) => {
    updateOwnComponentData({
      componentId,
      [fieldId]: newValue,
    });
  };

  const { mutate: updateOwnComponentData, isLoading: isSaving } = useUpdateComponentOwnData({
    onSuccess: () => {
      // Invalidate the query to refetch the component details
      queryClient.invalidateQueries({ queryKey: ['components', componentId] });

      // Invalidate the query to refetch components
      queryClient.invalidateQueries({ queryKey: ['commponents'] });
    },
    onError: () => {
      // Emit an event to the InplaceEdit component to rollback to the previous value
      window.dispatchEvent(new Event(`inplace-edit:${activeFieldId}:rollback`));
    },
    onSettled: deactivate,
  });

  return (
    <InplaceEditContext.Provider
      value={{
        componentId,
        setComponentId,
        activeFieldId,
        activate,
        deactivate,
        handleSave,
        isSaving,
      }}
    >
      {children}
    </InplaceEditContext.Provider>
  );
};

export default ComponentDetailsPageInplaceEditProvider;
